import { useNuxtApp, useState } from "#imports";
import { useAuthBootstrap } from "~/composables/useAuthBootstrap";

export const useFirstOrderDiscount = () => {
  const { $useOneCApi } = useNuxtApp();
  const { isAuthorized, readGuidFromStorage } = useAuthBootstrap();
  const hasOrders = useState<boolean | null>(
    "first-order:has-orders",
    () => null,
  );
  const client = useState("first-order:client", () => "");
  const loading = useState("first-order:loading", () => false);
  const revision = useState("first-order:revision", () => 0);

  const reset = () => {
    revision.value++;
    hasOrders.value = null;
    client.value = "";
    loading.value = false;
  };

  const storageKey = (guid: string) => `apsh:client-has-orders:${guid}`;

  const saveResult = (guid: string, value: boolean) => {
    try {
      sessionStorage.setItem(storageKey(guid), value ? "1" : "0");
    } catch {
      // Storage can be unavailable in restricted browser modes.
    }
  };

  const markOrderPlaced = () => {
    revision.value++;
    hasOrders.value = true;
    loading.value = false;
    if (import.meta.client) {
      const guid = readGuidFromStorage();
      if (guid) saveResult(guid, true);
    }
  };

  const refresh = async () => {
    if (!import.meta.client || !isAuthorized.value) return;
    const guid = readGuidFromStorage();
    if (!guid) return;
    if (client.value !== guid) {
      reset();
      client.value = guid;
    }
    if (loading.value) return;
    try {
      const cached = sessionStorage.getItem(storageKey(guid));
      if (cached === "0" || cached === "1") {
        hasOrders.value = cached === "1";
        return;
      }
    } catch {
      // Fall back to the current page state if session storage is unavailable.
      if (hasOrders.value !== null) return;
    }

    const requestRevision = revision.value;
    loading.value = true;
    // Hide an old offer while checking whether it is still available.
    hasOrders.value = null;
    try {
      const response = (await $useOneCApi("getClientHasOrders")) as {
        Статус?: string;
        ЕстьЗаказы?: unknown;
      };
      if (
        revision.value !== requestRevision ||
        !isAuthorized.value ||
        readGuidFromStorage() !== guid
      )
        return;

      const flag = response?.ЕстьЗаказы;
      if (response?.Статус === "OK" && (flag === "0" || flag === 0)) {
        hasOrders.value = false;
      } else if (response?.Статус === "OK" && (flag === "1" || flag === 1)) {
        hasOrders.value = true;
      }
      if (hasOrders.value !== null) saveResult(guid, hasOrders.value);
    } catch {
      // An unavailable or invalid response must never advertise a discount.
    } finally {
      if (revision.value === requestRevision) loading.value = false;
    }
  };

  return { hasOrders, client, refresh, reset, markOrderPlaced };
};
